/**
 * What the strip map MARKS, and in what colour, decided WITHOUT a canvas (08 §2.7, §4).
 *
 * The map audit of ROUND5 found one marker channel - `target: true`, painted red - doing every job:
 * "destroy this" on fifteen things the briefing forbids destroying, the grey of a job done on a target
 * that had just left and lost the mission, and nothing at all on the two men a mission is actually
 * about, because they move and every mark was a static prop. Every decision here is a pure function so
 * that the test can hold the map to the orders text without a drawing context - `graveOf` and
 * `extraMarks` set that pattern in ROUND4 and it earned its keep. The static GROUND is `mapGround.ts`.
 *
 * Nothing here names an archetype: a mark's meaning comes from the objective's kind and rules (10 §3).
 */

import { targetMark } from '../../sim/objectives/target';
import type { MissionDef, ObjectiveKind, World } from '../../sim/types';

/** Every colour the strip map paints with. Kept beside the decisions so one file says what a mark MEANS. */
export const MAP_COLOURS = {
  water: 'rgba(63, 127, 166, 0.55)',
  /** A frozen barrier reads paler than open water, as it does in the world (05 §5.1). */
  ice: 'rgba(150, 194, 219, 0.5)',
  /** A ford, or ice thick enough to trust: the pale lane the orders tell him to cross on. */
  crossing: '#cfe4ef',
  /** Cracked ice: a hole in the band, because that is what it becomes under a runner. */
  weakIce: '#14121a',
  road: 'rgba(91, 107, 58, 0.9)',
  trench: 'rgba(242, 240, 230, 0.3)',
  goal: '#ffb000',
  /** Where the mission wants him standing: the targets he is to reach or work, and its own zones. */
  waypoint: '#3fd8c8',
  /** The zones in the same hue, held back, so the mark inside one still reads as the thing itself. */
  waypointZone: 'rgba(63, 216, 200, 0.6)',
  /** A rule that loses the mission - a fail radius. The HUD's own warning orange. */
  danger: '#f2701d',
  /** A marked minefield: the same warning, held well back, because it is ground and not a boundary. */
  mine: 'rgba(242, 112, 29, 0.6)',
  target: '#e0584c',
  /** The colour-blind-safe enemy accent (05 §4.3), lifted like `--hud-red-hi`. */
  targetSafe: '#7fb2f5',
  targetCore: '#f2f0e6',
  wreck: '#6b7280',
  pickup: '#f2f0e6',
  pod: '#7cff6b',
  pickupLive: '#7cff6b',
  /** People to bring home: the warm off-white of the pickup X, because they are going to it. */
  captive: '#f2f0e6',
  /** A fallen ranger's marker: amber, like everything else that is YOURS. Never red - it is not a target. */
  tags: '#ffb000',
  ranger: '#ffb000',
  ink: '#14121a',
  /** The pipe across the map: steel, and neither a road nor a trench. */
  pipe: '#8a8f99',
  /**
   * Recon Photos (04 §8): the buildings, as blocks of stone laid under everything else. Warm grey
   * because nothing else on the map is, and held back hard: forty buildings can stand inside a
   * compound 20 px wide, and at any stronger they would bury the one target mark inside them.
   */
  structure: 'rgba(158, 146, 128, 0.5)',
  /**
   * Patrol Schedule (04 §8): the rounds the sentries walk. Violet, which nothing else on this map
   * uses - a route must not be mistaken for the road it may follow (olive), a trench (faint white),
   * or anything the mission is judged by (amber, teal, orange). It is drawn as a dashed closed ring
   * so that it reads as a ROUND and not as a way from one place to another.
   */
  patrol: 'rgba(183, 130, 226, 0.95)',
} as const;

/**
 * A mark the live layer paints beyond the targets and the pods. `man` is the one the mission is about
 * while he can still fight back; `body` is the same man once he is down and there is something on him
 * left to take - the shape stays, the colour changes with the job (ROUND5 §2.7).
 */
export interface ExtraMark {
  kind: 'captive' | 'tags' | 'man' | 'body';
  x: number;
  z: number;
}

/** Where a mission's fallen-ranger marker stands, if it carries one. Pure. */
export function graveOf(def: MissionDef): { x: number; z: number } | null {
  const tags = def.dogTags;
  if (tags === undefined) return null;
  const prop = def.props.find((p) => p.id === tags.propId);
  return prop === undefined ? null : { x: prop.x, z: prop.z };
}

/**
 * The people, the grave and the man of the mission, worth marking this frame.
 *
 * A captive is marked while he is still HELD (in his pit, behind the wire) or WAITING to be met -
 * both of which stand still. Once he is walking with the ranger he is at the ranger's own mark and
 * marking him again would only blot it. A grave is marked until the tags are in hand.
 *
 * The man comes last because he MOVES: the general walks his round and is then hurried into his car,
 * the courier rides from the moment the convoy wakes. `sim/objectives/target.targetMark` puts the
 * mark on the vehicle while he is aboard it, and says when he is down but not yet searched. Pure.
 */
export function extraMarks(world: World, grave: { x: number; z: number } | null): ExtraMark[] {
  const out: ExtraMark[] = [];
  for (const c of world.objective.captives) {
    if (c.state === 'held' || c.state === 'waiting') out.push({ kind: 'captive', x: c.x, z: c.z });
  }
  if (grave !== null && !world.objective.tagsRecovered) out.push({ kind: 'tags', x: grave.x, z: grave.z });
  const man = targetMark(world);
  if (man !== null) out.push({ kind: man.down ? 'body' : 'man', x: man.x, z: man.z });
  return out;
}

/** Mission metres to map pixels. One place, because the repaint key must round exactly as the paint does. */
export const toPixel = (v: number, scale: number): number => Math.round(v * scale);

/**
 * Hot Extraction (04 §7): the pickup is CONTESTED. When the Osprey turns inbound the garrison goes to a
 * full alarm, the nearest squad makes for the X and the aircraft holds over it for less long
 * (`INTEG3.hotHoverFactor`). The orders and the briefing have both said so since ROUND4 and the map never
 * did, though the X is where he runs when everything has gone wrong. Presentation only, so the HUD reads
 * the modifier itself, exactly as it does for Radio Silence (`sim/conditions.ts`).
 */
export const hotPickup = (def: MissionDef): boolean => def.modifiers.includes('hot-extraction');

/**
 * The broken ring that says so: eight pixels at radius 3, as offsets from the X's centre. Radius 3
 * because the X's arms reach (+-2, +-2) - a tighter ring would sit on its tips and make a blob of the
 * one mark on the map he looks for when it has all gone wrong. Shape, not a third colour: the X is
 * already white-then-green and that pair means "the Osprey is coming", which is a different sentence.
 */
export const HOT_RING: ReadonlyArray<readonly [number, number]> = [
  [-3, -3], [0, -3], [3, -3], [-3, 0], [3, 0], [-3, 3], [0, 3], [3, 3],
];

/**
 * The part of the repaint key the extra marks own.
 *
 * A count was enough while everything in the list stood still. It is not: the man of a `killTarget` or
 * `lootTarget` mission walks his round and is then driven off, and a count would have drawn him once and
 * left him there for the rest of the mission. The key is what is DRAWN - the rounded pixel - so a mark
 * that has not moved a pixel costs nothing and one that has cannot be missed.
 */
export function marksKey(marks: readonly ExtraMark[], scale: number): string {
  return marks.map((m) => `${m.kind[0]}${toPixel(m.x, scale)}:${toPixel(m.z, scale)}`).join('');
}

/** What a flagged prop is FOR: a thing to blow up, or a place to get to and work. */
export type TargetRole = 'destroy' | 'reach';

/** A flagged prop and what the objective asks of it. */
export interface TargetMark {
  id: number;
  x: number;
  z: number;
  role: TargetRole;
}

/**
 * The objective kinds whose targets are destroyed. Every other kind asks him to REACH the thing and
 * do something at it - photograph it, work it, meet the man beside it - and fifteen archetypes were
 * painting those red, which is the map telling him to blow up the code book, the safe, the exchange,
 * the laboratory and a friendly partisan camp (ROUND5 §1.4). The kind decides, never the archetype.
 */
const DESTROY_KINDS: readonly ObjectiveKind[] = ['destroyTargets', 'plantAtTarget', 'plantAtTargets'];

/**
 * A flagged prop that the objective does not list is a PROXY for something else - the officer's tent,
 * the cut in the wire, the guard house with the detonator - so it is a place to reach even on a
 * demolition mission, where its neighbours are things to destroy.
 */
export function targetRole(def: MissionDef, propId: number): TargetRole {
  if (!DESTROY_KINDS.includes(def.objective.kind)) return 'reach';
  return def.objective.targetPropIds.includes(propId) ? 'destroy' : 'reach';
}

/** Every prop the map marks, with its role. Pure. */
export function targetMarks(def: MissionDef): TargetMark[] {
  return def.props
    .filter((prop) => prop.target === true)
    .map((prop) => ({ id: prop.id, x: prop.x, z: prop.z, role: targetRole(def, prop.id) }));
}

/**
 * How a marked prop stands now. `removed` matters on its own: `targetsLeave` sets it as well as
 * `destroyed`, so the train that has just LOST the mission was being painted the grey of a job done
 * (ROUND5 §1.3). Gone whole is not wrecked.
 */
export type TargetState = 'live' | 'wrecked' | 'gone';

export function targetStateOf(state: { destroyed: boolean; removed?: boolean } | undefined): TargetState {
  if (state === undefined) return 'live';
  if (state.removed === true) return 'gone';
  return state.destroyed ? 'wrecked' : 'live';
}
